/**
 * Design System Colors - Single Source of Truth
 *
 * Define your brand colors once here, and they'll be used by:
 * - Mantine components
 * - Tailwind utilities
 * - Any custom styling
 */

export const colors = {
  // Brand primary hues (Superbar Product Design System 2.0) — distinct from Mantine primary/secondary scales
  brandPrimary: {
    orange: {
      light: '#FEEAD7',
      medium: '#FAA857',
      dark: '#FA5616',
    },
    purple: {
      light: '#E7DFFD',
      medium: '#855DF4',
      dark: '#5728D9',
    },
    blue: {
      light: '#DCE3FD',
      medium: '#5071F5',
      dark: '#0429BD',
    },
  },
  primary: {
    50: '#f0f4ff',
    100: '#e0e9ff',
    200: '#c7d6fe',
    300: '#a5b9fc',
    400: '#8196f8',
    500: '#6370f0', // Main brand color
    600: '#4f4fe4',
    700: '#4240c8',
    800: '#3736a3',
    900: '#323481',
    950: '#1f1f4e',
  },
  secondary: {
    50: '#f4f7fb',
    100: '#e8eff6',
    200: '#cbd6ea',
    300: '#9eb5d7',
    400: '#698ebf',
    500: '#4871a8', // Main brand color
    600: '#36598d',
    700: '#2d4873',
    800: '#293e60',
    900: '#263651',
    950: '#1a2436',
  },
  // Ink - Text colors
  ink: {
    primary: '#111111',
    secondary: '#3A3A3A',
    secondaryMuted: '#525252', // neutral.dark.500
    tertiary: '#6A6A6A',
    tertiaryMuted: '#737373', // neutral.dark.400
    quaternary: '#B0B0B0',
    inverse: '#F5F5F5',
  },
  // Surface - Background colors
  surface: {
    0: '#FFFFFF',
    1: '#F7F7F7',
    2: '#EFEFEF',
    3: '#C9C9C9',
    dark: '#0F0F10',
    light: '#FFFFFF',
  },
  // Stroke - Border colors
  stroke: {
    light: '#E6E6E6',
    medium: '#C9C9C9',
    dark: '#4A4A4A',
  },
  neutral: {
    light: {
      0: '#000000',
      25: '#FAFAFA',
      50: '#F5F5F5',
      100: '#E2E2E2',
      200: '#D4D4D4',
      300: '#A1A1A1',
    },
    dark: {
      400: '#787878',
      500: '#5A5A5A',
      600: '#4C4C4C',
      700: '#363636',
      800: '#1E1E1E',
      900: '#0A0A0A',
    },
  },
  error: {
    25: '#FFF3F3',
    50: '#FFC7C9',
    100: '#FF5561',
    200: '#E72323',
    300: '#AD0000',
    400: '#4D0004',
  },
  success: {
    25: '#E8FFEE',
    50: '#A5F3C0',
    100: '#09CE7C',
    200: '#009C62',
    300: '#006343',
    400: '#002E20',
  },
  // Feedback - Status colors
  feedback: {
    success: {
      primary: '#0CBA4E',
      secondary: '#DBEFE6',
    },
    warning: {
      primary: '#E5A100',
      secondary: '#FFF8E6',
    },
    info: {
      primary: '#2196F3',
      secondary: '#E3F2FD',
    },
  },
} as const;

/**
 * CSS gradient strings for inline styles
 * Use these when you need a CSS gradient value (e.g., for borders, backgrounds)
 * For SVG-based decorative gradients, use the Gradient component instead.
 */
export const gradients = {
  brand:
    'linear-gradient(157.5deg, #5071F5 0%, #5728D9 33%, #855DF4 66%, #FAA857 100%)',
  brandCompact:
    'linear-gradient(157.5deg, #5071F5 0%, #5728D9 15%, #855DF4 30%, #FAA857 100%)',
} as const;

/**
 * Stop list for the 3-stop brand gradient used by SVG icons
 * (blue -> purple -> orange). Consume inside an SVG `<linearGradient>`.
 * Sourced from `brandPrimary.*.medium` so any hue change flows through.
 */
export const brandGradientStops = [
  { offset: 0, color: colors.brandPrimary.blue.medium },
  { offset: 0.5, color: colors.brandPrimary.purple.medium },
  { offset: 1, color: colors.brandPrimary.orange.medium },
] as const;

/**
 * Convert color object to Mantine color tuple format
 * Mantine expects: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]
 */
export function toMantineColorTuple(
  colorScale: Record<string, string>
): [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
] {
  return [
    colorScale['50'],
    colorScale['100'],
    colorScale['200'],
    colorScale['300'],
    colorScale['400'],
    colorScale['500'],
    colorScale['600'],
    colorScale['700'],
    colorScale['800'],
    colorScale['900'],
  ];
}
